import { useEffect, useRef, useState } from "react";

export const RETRASO_FILTROS_MS = 350;

/**
 * Retrasa la propagación de un valor hasta que deja de cambiar durante `retrasoMs`.
 *
 * Los listados vuelven a consultar la API cada vez que cambia su objeto de filtros. Sin este
 * retraso, escribir "18606" en un filtro de texto dispara seis peticiones al endpoint más caro
 * del sistema, de las cuales solo la última interesa.
 *
 * La comparación es por contenido y no por referencia: los objetos de filtros se recrean en cada
 * render, y comparar por referencia haría que el temporizador se reinicie para siempre.
 *
 * El valor inicial se devuelve sin retraso, para no demorar la primera carga de la página. Eso se
 * resuelve comparando contra el valor ya devuelto, y no contando renders: cuántas veces corre el
 * efecto antes del primer cambio depende de cuándo React vacíe los efectos, y no es de fiar.
 */
export function useDebouncedValue<T>(valor: T, retrasoMs: number = RETRASO_FILTROS_MS): T {
    const [valorRetrasado, setValorRetrasado] = useState<T>(valor);
    const valorActual = useRef<T>(valor);
    const serializado = JSON.stringify(valor);
    const serializadoDevuelto = useRef<string>(serializado);

    valorActual.current = valor;

    useEffect(() => {
        // El valor que se está devolviendo ya es este: en el montaje, o porque el temporizador
        // anterior alcanzó a aplicarlo. No hay nada que esperar.
        if (serializadoDevuelto.current === serializado) {
            return;
        }

        const temporizador = setTimeout(() => {
            serializadoDevuelto.current = serializado;
            setValorRetrasado(valorActual.current);
        }, retrasoMs);

        return () => clearTimeout(temporizador);
    }, [serializado, retrasoMs]);

    return valorRetrasado;
}

export default useDebouncedValue;
